import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Search } from "lucide-react";
import { useOrgContext } from "@/context/OrgContext";
import { useLocale } from "@/context/LanguageContext";
import { RefreshIconButton } from "@/components/ui/RefreshButton";
import {
  GenerateReportDialog,
  RegenerateConfirmDialog,
  DeleteReportConfirmDialog,
  ReportsEmptyState,
  ReportsTable,
  useOrgReports,
  useRegenerateOrgReport,
  useDeleteOrgReport,
  REPORT_STATUS_TABS,
  REPORTS_PAGE_SIZE,
  VALID_REPORT_STATUS_VALUES,
  DEFAULT_SORT,
  isReportSortKey,
  isSortDirection,
  nextSortState,
  sortReports,
  type ReportSortKey,
  type SortState,
} from "@/features/reports";
import {
  ReportStatus,
  type OrgReportListItemFragment,
} from "@/graphql/generated";

/**
 * HR engagement report list + generate flow. Reports are async — we lean
 * on `useOrgReports` to poll while any row is pending/generating.
 *
 * Sort, status filter, and search are URL-synced (?sort, ?dir, ?status, ?q)
 * so admins can share a specific view (e.g. "all failed quarterly reports").
 */
export function ReportsPage() {
  const { currentOrgId } = useOrgContext();
  const { t } = useLocale();
  const [dialogOpen, setDialogOpen] = useState(false);

  // ─── URL ↔ State Sync ─────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();

  const initialSort: SortState = useMemo(() => {
    const k = searchParams.get("sort");
    const d = searchParams.get("dir");
    if (k && isReportSortKey(k) && d && isSortDirection(d)) {
      return { key: k, direction: d };
    }
    return DEFAULT_SORT;
  }, []); // intentional: read once on mount; updates flow through setSearchParams

  const initialStatus: ReportStatus | null = useMemo(() => {
    const s = searchParams.get("status");
    if (s && VALID_REPORT_STATUS_VALUES.has(s)) return s as ReportStatus;
    return null;
  }, []);

  const initialQuery = searchParams.get("q") ?? "";

  const [sort, setSort] = useState<SortState>(initialSort);
  const [statusFilter, setStatusFilter] = useState<ReportStatus | null>(initialStatus);
  const [search, setSearch] = useState(initialQuery);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQuery);
  const [limit, setLimit] = useState(REPORTS_PAGE_SIZE);

  // Persist current filter/sort/search to the URL.
  useEffect(() => {
    const next = new URLSearchParams();
    if (sort.key !== DEFAULT_SORT.key || sort.direction !== DEFAULT_SORT.direction) {
      next.set("sort", sort.key);
      next.set("dir", sort.direction);
    }
    if (statusFilter) next.set("status", statusFilter);
    if (debouncedSearch.trim()) next.set("q", debouncedSearch.trim());
    setSearchParams(next, { replace: true });
  }, [sort, statusFilter, debouncedSearch, setSearchParams]);

  // Debounce the search input (300 ms — matches Challenges/Events).
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ─── Data ─────────────────────────────────────────────────────────────
  const { data, loading, error, refetch } = useOrgReports(currentOrgId, limit);
  const [regenerate, { loading: regenerating }] = useRegenerateOrgReport();
  const [regenerateTarget, setRegenerateTarget] =
    useState<OrgReportListItemFragment | null>(null);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  const [deleteReport, { loading: deleting }] = useDeleteOrgReport();
  const [deleteTarget, setDeleteTarget] =
    useState<OrgReportListItemFragment | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const allReports: OrgReportListItemFragment[] = data?.orgReports ?? [];

  const visibleReports = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const filtered = allReports.filter((r) => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (q && !r.periodLabel.toLowerCase().includes(q)) return false;
      return true;
    });
    return sortReports(filtered, sort);
  }, [allReports, statusFilter, debouncedSearch, sort]);

  // Heuristic: if the server returned a full page, there *might* be more.
  // Once a fetch returns less than the requested limit we stop offering it.
  const hasMore = !loading && allReports.length >= limit;

  // ─── Handlers ─────────────────────────────────────────────────────────
  const handleSortChange = useCallback((clicked: ReportSortKey) => {
    setSort((prev) => nextSortState(prev, clicked));
  }, []);

  const handleStatusChange = useCallback((value: ReportStatus | null) => {
    setStatusFilter(value);
  }, []);

  const handleLoadMore = useCallback(() => {
    setLimit((prev) => prev + REPORTS_PAGE_SIZE);
  }, []);

  const handleRegenerateRequest = useCallback(
    (report: OrgReportListItemFragment) => {
      setRegenerateError(null);
      setRegenerateTarget(report);
    },
    [],
  );

  const handleDeleteRequest = useCallback(
    (report: OrgReportListItemFragment) => {
      setDeleteError(null);
      setDeleteTarget(report);
    },
    [],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteReport({ variables: { reportId: deleteTarget._id } });
      setDeleteTarget(null);
      await refetch();
    } catch (error) {
      const apolloError = error as {
        graphQLErrors?: Array<{ extensions?: { code?: string } }>;
      };
      const code = apolloError?.graphQLErrors?.[0]?.extensions?.code;
      if (code === "VALIDATION_ERROR") {
        setDeleteError(t("reports.error.deleteInFlight"));
      } else if (code === "FORBIDDEN") {
        setDeleteError(t("reports.error.forbidden"));
      } else if (code === "NOT_FOUND") {
        setDeleteError(t("reports.error.notFound"));
      } else {
        setDeleteError(t("reports.error.generic"));
      }
    }
  }, [deleteTarget, deleteReport, refetch, t]);

  const handleRegenerateConfirm = useCallback(async () => {
    if (!regenerateTarget) return;
    setRegenerateError(null);
    try {
      await regenerate({ variables: { reportId: regenerateTarget._id } });
      setRegenerateTarget(null);
      await refetch();
    } catch (error) {
      const apolloError = error as {
        graphQLErrors?: Array<{ extensions?: { code?: string } }>;
      };
      const code = apolloError?.graphQLErrors?.[0]?.extensions?.code;
      if (code === "REPORT_RATE_LIMIT_EXCEEDED") {
        setRegenerateError(t("reports.error.rateLimit"));
      } else if (code === "VALIDATION_ERROR") {
        setRegenerateError(t("reports.error.regenerateInFlight"));
      } else if (code === "FORBIDDEN") {
        setRegenerateError(t("reports.error.forbidden"));
      } else {
        setRegenerateError(t("reports.error.generic"));
      }
    }
  }, [regenerateTarget, regenerate, refetch, t]);

  // ─── Render ───────────────────────────────────────────────────────────
  const hasAnyReports = allReports.length > 0;
  const showEmptyState = !loading && !hasAnyReports && !error;
  const noMatch = hasAnyReports && visibleReports.length === 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            {t("reports.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("reports.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
        >
          {t("reports.cta.generate")}
        </button>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {t("reports.error.loading")}
        </div>
      ) : null}

      {/* Filter + Search toolbar — only shown once there are reports to act on. */}
      {hasAnyReports ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <label htmlFor="reports-search" className="sr-only">
              {t("reports.search.label")}
            </label>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              id="reports-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("reports.search.placeholder")}
              className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div
            role="group"
            aria-label={t("reports.filter.label")}
            className="flex items-center gap-1 rounded-lg border border-border bg-muted/30 p-1"
          >
            {REPORT_STATUS_TABS.map((tab) => {
              const active = statusFilter === tab.value;
              return (
                <button
                  key={tab.labelKey}
                  type="button"
                  aria-pressed={active}
                  onClick={() => handleStatusChange(tab.value)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t(tab.labelKey)}
                </button>
              );
            })}
          </div>

          <RefreshIconButton
            onRefresh={async () => {
              await refetch();
            }}
            loading={loading}
          />
        </div>
      ) : null}

      {showEmptyState ? (
        <ReportsEmptyState onGenerate={() => setDialogOpen(true)} />
      ) : null}

      {regenerateError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {regenerateError}
        </div>
      ) : null}

      {deleteError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {deleteError}
        </div>
      ) : null}

      {hasAnyReports && !noMatch ? (
        <ReportsTable
          reports={visibleReports}
          sort={sort}
          onSortChange={handleSortChange}
          onRegenerate={handleRegenerateRequest}
          onDelete={handleDeleteRequest}
        />
      ) : null}

      {noMatch ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
          <p className="text-sm font-medium text-foreground">
            {t("reports.empty.noMatchTitle")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("reports.empty.noMatchHint")}
          </p>
        </div>
      ) : null}

      {hasAnyReports && hasMore && !noMatch ? (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={handleLoadMore}
            disabled={loading}
            className="inline-flex items-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground shadow-sm transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? t("common.loading") : t("reports.action.loadMore")}
          </button>
        </div>
      ) : null}

      <GenerateReportDialog
        open={dialogOpen}
        organizationId={currentOrgId}
        existingReports={allReports}
        onClose={() => setDialogOpen(false)}
        onCreated={() => {
          void refetch();
        }}
      />

      <RegenerateConfirmDialog
        open={regenerateTarget !== null}
        periodLabel={regenerateTarget?.periodLabel ?? ""}
        loading={regenerating}
        onConfirm={handleRegenerateConfirm}
        onClose={() => {
          if (regenerating) return;
          setRegenerateTarget(null);
          setRegenerateError(null);
        }}
      />

      <DeleteReportConfirmDialog
        open={deleteTarget !== null}
        periodLabel={deleteTarget?.periodLabel ?? ""}
        loading={deleting}
        onConfirm={handleDeleteConfirm}
        onClose={() => {
          if (deleting) return;
          setDeleteTarget(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
}
